import React, { useState, useEffect, useMemo } from 'react';
import SEO from '../SEO/SEO';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  UserGroupIcon,
  CurrencyRupeeIcon,
  ClockIcon,
  ChartBarIcon,
  DocumentTextIcon,
  AcademicCapIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  ArrowRightIcon,
  BanknotesIcon,
  HomeIcon,
  BuildingOfficeIcon,
  UserIcon,
  BriefcaseIcon,
  ShieldCheckIcon,
  DocumentCheckIcon,
  SparklesIcon,
  LightBulbIcon,
  PresentationChartLineIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from "react-router-dom";
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import EnquiryModal from '../Modals/EnquiryModal';
import { validateEmail, validateMobile } from '../../utils/helpers';
import './DSAOnboarding.css';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const dataURLtoFile = (dataurl, filename) => {
  try {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  } catch (error) {
    console.error("Error converting data URL to File:", error);
    return null;
  }
};

const sanitizeAndFormatHtml = (content) => {
  if (!content || typeof content !== 'string') return '';
  let html = content;
  html = html.replace(/<a\s+[^>]*?>/gi, (tag) => {
    const attrMatch = tag.match(/(?:["']?(?:link|href)\s*=\s*\\?["']?\s*)([^"'\\>]+?)(?:\s*\\?["']?\s*)(?:>|\s|$)/i);
    if (!attrMatch) return tag;
    const rawUrl = attrMatch[1].trim().replace(/^["']|["']$/g, '');
    const targetMatch = tag.match(/target\s*=\s*["']?([^"'>\s]+)["']?/i);
    const targetAttr = targetMatch ? ` target="${targetMatch[1]}"` : '';
    return `<a href="${rawUrl}"${targetAttr}>`;
  });
  if (!/<(?:br|p|div)\b/i.test(html)) {
    html = html.replace(/\r?\n/g, '<br />');
  }
  return html;
};

const DSAPartnerProgram = () => {
  const handleRichTextClick = (e) => {
    const target = e.target.closest('a');
    if (!target) return;
    const href = target.getAttribute('href');
    const linkTarget = target.getAttribute('target');
    if (!href || linkTarget === '_blank') return;

    if (href.startsWith('/') && !href.startsWith('//')) {
      e.preventDefault();
      navigate(href);
    } else if (href.startsWith('https://ahamhfc.com/') || href.startsWith('http://ahamhfc.com/')) {
      const path = href.replace(/^https?:\/\/ahamhfc\.com/, '');
      if (path.startsWith('/')) {
        e.preventDefault();
        navigate(path);
      }
    }
  };

  const [activeFAQ, setActiveFAQ] = useState(0);
  const [isFetchingDigiLocker, setIsFetchingDigiLocker] = useState(false);
  const [signzyRequestId, setSignzyRequestId] = useState(null);

  // Signzy Callback Listener & Profile Image Restoration
  useEffect(() => {
    // Restore profile image if stored in localStorage
    const savedBase64 = localStorage.getItem('dsa_profile_image_base64');
    const savedFilename = localStorage.getItem('dsa_profile_image_filename');
    if (savedBase64 && savedFilename) {
      const file = dataURLtoFile(savedBase64, savedFilename);
      if (file) {
        setRegistrationData(prev => ({ ...prev, profileImage: file }));
      }
    }

    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get('status');
    const requestIdFromUrl = urlParams.get('requestid');
    const existingRequestId = localStorage.getItem('signzy_request_id');

    // prioritize requestId from URL, fallback to localStorage
    const requestId = requestIdFromUrl || existingRequestId;

    if (status === 'success' && requestId) {
      handleSignzyCallback(requestId);

      // Clean up the URL to prevent re-triggering on refresh
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleSignzyCallback = async (requestId) => {
    setIsFetchingDigiLocker(true);
    const loadingToast = toast.loading("Verifying your DigiLocker documents...");

    try {
      const response = await axios.post('/api/signzy/fetch-documents', { requestId });
      if (response.data.success) {
        // Signzy response structure can vary, let's log it to be sure
        console.log("Signzy Fetch Response:", response.data);

        const signzyData = response.data.data || {};
        const results = signzyData.result || signzyData;

        // Find the files array in the nested structure
        const files = results.files || results.items || [];

        // Find metadata (Patron info / Aadhaar info)
        const identities = results.identities || signzyData.identities || [];
        const patron = results.patron || signzyData.patron || identities[0] || {};
        const meta = results.metaData || signzyData.metaData || patron || {};

        // In Signzy DigiLocker, actual file URLs are often in identities, while items/files just have the names/URIs
        const allFiles = [...(results.files || []), ...(results.items || []), ...identities];

        const newDocs = { ...documents };

        allFiles.forEach(file => {
          const fileName = file.name || file.fileName || file.docType || '';
          const fileData = {
            ...file, // Send all properties to backend for robust extraction
            name: fileName,
            url: file.directLink || file.link || file.url || file.file || file.downloadUrl || file.documentLink || file.pdfUrl || file.documentUrl || file.pdf,
            source: 'digilocker',
            fileId: file.fileId || file.id || file.documentId || file.uri,
            // ── savedFilename is injected by signzy.js fetchDigiLockerDocument() ──
            // If set, dsaEmail.js will use this directly (no re-download needed)
            savedFilename: file.savedFilename || null
          };


          const lowerName = fileName.toLowerCase();
          if (lowerName.includes('aadhaar') || lowerName.includes('adhar')) {
            newDocs.proofOfIdentity = fileData;
            // Aadhaar often serves as address proof too
            newDocs.proofOfAddress = fileData;
          } else if (lowerName.includes('pan')) {
            newDocs.panCard = fileData;
          } else if (lowerName.includes('voter') || lowerName.includes('driving')) {
            newDocs.proofOfAddress = fileData;
          }
        });

        setDocuments(newDocs);

        // Populate Basic Data from metadata
        setBasicData(prev => ({
          ...prev,
          fullNameAsPan: meta.name || patron.name || prev.fullNameAsPan,
          dateOfBirth: meta.dob || patron.dob || prev.dateOfBirth,
          aadhaarNumber: meta.uid || patron.uid || meta.aadhaarNumber || prev.aadhaarNumber,
          panNumber: meta.pan || patron.pan || prev.panNumber
        }));

        setUploadMode('digilocker');
        localStorage.removeItem('signzy_request_id');
        toast.success("Verified documents retrieved from DigiLocker!");

        // Skip straight to Step 3 (Documents) so user sees the success
        setOtpVerified(true);
        setCurrentStep(3);

        // Scroll down to the registration form so the user isn't stuck at the top of the page
        setTimeout(() => {
          const element = document.getElementById('dsa-registration');
          if (element) {
            const offset = 100;
            const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
            window.scrollTo({
              top: elementPosition - offset,
              behavior: 'smooth'
            });
          }
        }, 500);
      }
    } catch (error) {
      console.error("Signzy Callback Error:", error);
      toast.error("Failed to retrieve documents from DigiLocker.");
    } finally {
      setIsFetchingDigiLocker(false);
      toast.dismiss(loadingToast);
    }
  };
  const [activeSection, setActiveSection] = useState('why-partner');
  const [isEnquiryModalOpen, setIsEnquiryModalOpen] = useState(false);
  const navigate = useNavigate();
  const [banner, setBanner] = useState(null);
  const [overview, setOverview] = useState(null);
  const [benefits, setBenefits] = useState([]);
  const [benefitsSettings, setBenefitsSettings] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [faqSettings, setFaqSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  // CTA Banner state
  const [ctaBannerSettings, setCtaBannerSettings] = useState({
    title: 'Ready to Start Your Partnership Journey?',
    description: 'Join hundreds of successful partners earning with Aham Housing Finance',
    button_text: 'Get Started Today',
    button_link: '#',
    bottom_text: 'Ready to partner with Aham? Complete your <a href="https://ahamhfc.com/home-loan-dsa-registration-online">Home Loan DSA registration online</a> to get started.'
  });
  const [ctaBannerLoading, setCtaBannerLoading] = useState(true);

  // Fetch Page Data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          bannerRes,
          overviewRes,
          benefitsRes,
          settingsRes,
          productsRes,
          productsSettingsRes,
          partnerTypesRes,
          partnerTypesSettingsRes,
          partnershipStepsRes,
          partnershipStepsSettingsRes,
          supportCardsRes,
          supportSettingsRes,
          devItemsRes,
          devSettingsRes,
          storiesRes,
          storiesSettingsRes,
          requirementsRes,
          requirementsSettingsRes,
          faqsRes,
          faqsSettingsRes,
          ctaBannerRes
        ] = await Promise.all([
          axios.get('/api/admin/banners/page/dsa_partner_program'),
          axios.get('/api/admin/loan-overview/dsa_partner_program'),
          axios.get('/api/admin/key-benefits/dsa_partner_program/active'),
          axios.get('/api/admin/key-benefits/settings/dsa_partner_program'),
          axios.get('/api/admin/high-demand-products/active'),
          axios.get('/api/admin/high-demand-products/settings'),
          axios.get('/api/admin/key-benefits/dsa_partner_types/active'),
          axios.get('/api/admin/key-benefits/settings/dsa_partner_types'),
          axios.get('/api/admin/key-benefits/dsa_partnership_steps/active'),
          axios.get('/api/admin/key-benefits/settings/dsa_partnership_steps'),
          axios.get('/api/admin/key-benefits/dsa_support_training/active'),
          axios.get('/api/admin/key-benefits/settings/dsa_support_training'),
          axios.get('/api/admin/key-benefits/dsa_ongoing_development/active'),
          axios.get('/api/admin/key-benefits/settings/dsa_ongoing_development'),
          axios.get('/api/admin/key-benefits/dsa_success_stories/active'),
          axios.get('/api/admin/key-benefits/settings/dsa_success_stories'),
          axios.get('/api/admin/key-benefits/dsa_registration_requirements/active'),
          axios.get('/api/admin/key-benefits/settings/dsa_registration_requirements'),
          axios.get('/api/faqs/dsa_faq/active'),
          axios.get('/api/faqs/settings/dsa_faq'),
          axios.get('/api/admin/cta-banner/settings/dsa_partner_program')
        ]);

        if (bannerRes.data.success && bannerRes.data.data) {
          const banners = bannerRes.data.data;
          if (Array.isArray(banners) && banners.length > 0) {
            setBanner(banners[0]);
          } else if (!Array.isArray(banners)) {
            setBanner(banners);
          }
        }
        if (overviewRes.data.success && overviewRes.data.data) {
          setOverview(overviewRes.data.data);
        }
        if (benefitsRes.data.success && benefitsRes.data.data) {
          setBenefits(benefitsRes.data.data);
        }
        if (settingsRes.data.success && settingsRes.data.data) {
          setBenefitsSettings(settingsRes.data.data);
        }
        if (productsRes.data.success && productsRes.data.data) {
          setHighDemandProducts(productsRes.data.data);
        }
        if (productsSettingsRes.data.success && productsSettingsRes.data.data) {
          setHighDemandSettings(productsSettingsRes.data.data);
        } else {
          setHighDemandSettings({
            section_title: 'High Demand Products',
            section_subtitle: 'We specialize in affordable housing loans for:'
          });
        }
        if (partnerTypesRes.data.success && partnerTypesRes.data.data) {
          setPartnerTypes(partnerTypesRes.data.data);
        }
        if (partnerTypesSettingsRes.data.success && partnerTypesSettingsRes.data.data) {
          setPartnerTypesSettings(partnerTypesSettingsRes.data.data);
        }
        if (partnershipStepsRes.data.success && partnershipStepsRes.data.data) {
          setPartnershipSteps(partnershipStepsRes.data.data);
        }
        if (partnershipStepsSettingsRes.data.success && partnershipStepsSettingsRes.data.data) {
          setPartnershipStepsSettings(partnershipStepsSettingsRes.data.data);
        }
        if (supportCardsRes.data.success && supportCardsRes.data.data) {
          setSupportCards(supportCardsRes.data.data);
        }
        if (supportSettingsRes.data.success && supportSettingsRes.data.data) {
          setSupportSettings(supportSettingsRes.data.data);
        }
        if (devItemsRes.data.success && devItemsRes.data.data) {
          setDevelopmentItems(devItemsRes.data.data);
        }
        if (devSettingsRes.data.success && devSettingsRes.data.data) {
          setDevelopmentSettings(devSettingsRes.data.data);
        }
        if (storiesRes.data.success && storiesRes.data.data) {
          setSuccessStories(storiesRes.data.data);
        }
        if (storiesSettingsRes.data.success && storiesSettingsRes.data.data) {
          setSuccessStoriesSettings(storiesSettingsRes.data.data);
        }
        if (requirementsRes.data.success && requirementsRes.data.data) {
          setRegistrationRequirements(requirementsRes.data.data);
        }
        if (requirementsSettingsRes.data.success && requirementsSettingsRes.data.data) {
          setRegistrationRequirementsSettings(requirementsSettingsRes.data.data);
        }
        if (faqsRes.data.success && faqsRes.data.data) {
          setFaqs(faqsRes.data.data);
        }
        if (faqsSettingsRes.data.success && faqsSettingsRes.data.data) {
          setFaqSettings(faqsSettingsRes.data.data);
        }
        if (ctaBannerRes.data.success && ctaBannerRes.data.data) {
          setCtaBannerSettings(ctaBannerRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching page data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Scroll Event Listener to auto-switch active section
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['why-partner', 'who-can-join', 'how-it-works', 'support-training', 'success-stories', 'resources', 'requirements', 'dsa-registration', 'faq'];

      sections.forEach((sec) => {
        const element = document.getElementById(sec);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            setActiveSection(sec);
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Multi-Step State
  const [currentStep, setCurrentStep] = useState(0); // 0=Registration, 1=Basic, 2=Bank, 3=Documents, 4=Agreement
  const [otpVerified, setOtpVerified] = useState(false);

  // Registration Form State
  const [registrationData, setRegistrationData] = useState(() => {
    const saved = localStorage.getItem('dsa_reg_data');
    return saved ? JSON.parse(saved) : {
      fullName: '',
      email: '',
      mobile: '',
      pinCode: '',
      city: '',
      businessType: 'Individual',
      address: '',
      profileImage: null
    };
  });

  // Basic Details Form State
  const [basicData, setBasicData] = useState(() => {
    const saved = localStorage.getItem('dsa_basic_data');
    return saved ? JSON.parse(saved) : {
      gstCertification: '',
      panNumber: '',
      fullNameAsPan: '',
      dateOfBirth: '',
      aadhaarNumber: ''
    };
  });

  // Bank Details Form State
  const [bankData, setBankData] = useState(() => {
    const saved = localStorage.getItem('dsa_bank_data');
    return saved ? JSON.parse(saved) : {
      accountNumber: '',
      reEnterAccountNumber: '',
      ifscCode: '',
      bankBranch: '', // Auto-filled from IFSC
    };
  });
  const [ifscError, setIfscError] = useState('');

  // Document Upload State
  const [uploadMode, setUploadMode] = useState('manual'); // 'digilocker' or 'manual'
  const [documents, setDocuments] = useState({
    proofOfIdentity: null,
    proofOfAddress: null,
    panCard: null,
    cancelledCheque: null,
    gstCertificate: null
  });

  // OTP State
  const [otpSent, setOtpSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [otp, setOtp] = useState('');
  const [resendTimer, setResendTimer] = useState(0);

  // Resend timer countdown
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Submission Success State
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [dsaCode, setDsaCode] = useState('');
  const [assignedBranch, setAssignedBranch] = useState('');
  const [availableBranches, setAvailableBranches] = useState([]);

  // Fetch Branches for Assignment
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await axios.get('/api/admin/branches/active');
        if (response.data.success) {
          setAvailableBranches(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching branches:", error);
      }
    };
    fetchBranches();
  }, []);

  const uniqueCities = useMemo(() => {
    const cities = availableBranches.map(b => b.city?.trim()).filter(Boolean);
    const unique = [];
    const seen = new Set();
    for (const city of cities) {
      const lower = city.toLowerCase();
      if (lower.includes('chennai') || lower.includes('mount road')) continue; // Exclude Chennai & Chennai - Mount Road
      if (!seen.has(lower)) {
        seen.add(lower);
        unique.push(city);
      }
    }
    return unique.sort((a, b) => a.localeCompare(b));
  }, [availableBranches]);

  const maskAadhaar = (aadhaar) => {
    if (!aadhaar) return '';
    const clean = aadhaar.replace(/\D/g, '');
    if (clean.length !== 12) return aadhaar;
    return `XXXX XXXX ${clean.slice(8)}`;
  };


  // Registration Data Handler
  const handleRegistrationChange = (e) => {
    const { name, value } = e.target;
    setRegistrationData(prev => ({ ...prev, [name]: value }));
  };

  // Basic Details Data Handler
  const handleBasicChange = (e) => {
    const { name, value } = e.target;
    setBasicData(prev => ({ ...prev, [name]: value }));
  };

  // Bank Details Data Handler
  const handleBankChange = (e) => {
    const { name, value } = e.target;
    setBankData(prev => ({ ...prev, [name]: value }));
  };

  const handleSendOtp = async (e) => {
    if (e) e.preventDefault();

    if (!registrationData.fullName.trim()) return toast.error("Full Name is required");
    if (!validateEmail(registrationData.email)) return toast.error("Please enter a valid email");
    if (!validateMobile(registrationData.mobile)) return toast.error("Please enter a valid 10-digit mobile number");
    if (!registrationData.pinCode || registrationData.pinCode.length < 6) return toast.error("Please enter a valid 6-digit PIN code");
    if (!registrationData.city.trim()) return toast.error("Sourcing City is required");
    if (!registrationData.address.trim()) return toast.error("Address is required");
    if (!registrationData.profileImage) return toast.error("Profile Image is required");

    setIsSendingOtp(true);
    try {
      const response = await axios.post('/api/otp/send', { phone: registrationData.mobile });
      if (response.data.success) {
        setOtpSent(true);
        setResendTimer(120);
        toast.success("OTP sent successfully to your mobile number!");
      } else {
        toast.error(response.data.message || "Failed to send OTP");
      }
    } catch (error) {
      console.error("Send OTP error:", error);
      toast.error(error.response?.data?.message || "Error sending OTP. Please try again.");
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    if (e) e.preventDefault();

    if (!otp || otp.length !== 6) {
      return toast.error("Please enter a valid 6-digit OTP");
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post('/api/otp/verify', {
        phone: registrationData.mobile,
        otp: otp
      });

      if (response.data.success) {
        setOtpVerified(true);
        setCurrentStep(1); // Move to Basic Details step
        toast.success("OTP verified successfully! Please fill in your basic details.");
      } else {
        toast.error(response.data.message || "Invalid OTP");
      }
    } catch (error) {
      console.error("Verify OTP error:", error);
      toast.error(error.response?.data?.message || "Error verifying OTP");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBasicDetailsSubmit = (e) => {
    if (e) e.preventDefault();

    // Validation
    const panRegex = /^[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}$/;
    if (!basicData.panNumber || !panRegex.test(basicData.panNumber)) {
      return toast.error("Please enter a valid PAN number (e.g. ABCDE1234F)");
    }
    if (!basicData.fullNameAsPan.trim()) {
      return toast.error("Full Name (as per PAN) is required");
    }
    if (!basicData.dateOfBirth) {
      return toast.error("Date of Birth is required");
    }

    // Minimum 18 years old validation
    const dobDate = new Date(basicData.dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - dobDate.getFullYear();
    const m = today.getMonth() - dobDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dobDate.getDate())) {
      age--;
    }
    if (age < 18) {
      return toast.error("You must be at least 18 years old to register.");
    }
    if (!basicData.aadhaarNumber || basicData.aadhaarNumber.replace(/\s/g, '').length !== 12) {
      return toast.error("Please enter a valid 12-digit Aadhaar number");
    }
    if (basicData.gstCertification) {
      if (basicData.gstCertification.length !== 15 || !/^[A-Za-z0-9]+$/.test(basicData.gstCertification)) {
        return toast.error("GST Certification must be exactly 15 alphanumeric characters");
      }
    }

    // Move to next step
    setCurrentStep(2); // Move to Bank Details
    toast.success("Basic details saved! Please provide bank details.");
  };

  // IFSC Lookup Handler - Auto-fill bank/branch details
  const handleIfscLookup = async (ifscCode) => {
    setIfscError('');
    if (ifscCode.length === 11) {
      try {
        // Call IFSC API to get bank details
        const response = await fetch(`https://ifsc.razorpay.com/${ifscCode}`);

        if (response.ok) {
          const data = await response.json();
          const bankBranchInfo = `${data.BANK} — ${data.BRANCH}, ${data.CITY}`;

          setBankData(prev => ({
            ...prev,
            bankBranch: bankBranchInfo
          }));

          toast.success("Bank details fetched successfully!");
        } else {
          setIfscError("Invalid IFSC Code. Please check and try again.");
          setBankData(prev => ({
            ...prev,
            bankBranch: ''
          }));
        }
      } catch (error) {
        console.error("IFSC lookup error:", error);
        setIfscError("Unable to verify IFSC. Please check your connection.");
        setBankData(prev => ({
          ...prev,
          bankBranch: ''
        }));
      }
    }
  };

  const handleBankDetailsSubmit = (e) => {
    if (e) e.preventDefault();

    // Validation
    if (!bankData.accountNumber.trim()) {
      return toast.error("Account Number is required");
    }
    if (!bankData.reEnterAccountNumber.trim()) {
      return toast.error("Please re-enter Account Number for confirmation");
    }
    if (bankData.accountNumber !== bankData.reEnterAccountNumber) {
      return toast.error("Account Numbers do not match. Please check and try again.");
    }
    if (!bankData.ifscCode.trim() || bankData.ifscCode.length !== 11) {
      setIfscError("Please enter a valid 11-character IFSC code");
      return;
    }
    if (ifscError) {
      return;
    }
    if (!bankData.bankBranch.trim()) {
      setIfscError("Bank/Branch details not found. Please verify IFSC code.");
      return;
    }

    // Move to next step
    setCurrentStep(3); // Move to Documents
  };

  // Document Upload Handler
  const handleDocumentUpload = (docType, file, inputElement) => {
    if (file) {
      // Check file size (max 2MB = 2 * 1024 * 1024 bytes)
      if (file.size > 2 * 1024 * 1024) {
        toast.error("File size must be less than 2MB.");
        if (inputElement) inputElement.value = '';
        return;
      }
      // Check file type (only PDF allowed)
      if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
        toast.error("Only PDF files are allowed.");
        if (inputElement) inputElement.value = '';
        return;
      }
      setDocuments(prev => ({
        ...prev,
        [docType]: file
      }));
      const docNames = {
        proofOfIdentity: 'Proof of Identity',
        proofOfAddress: 'Proof of Address',
        panCard: 'PAN Card',
        cancelledCheque: 'Cancelled Cheque',
        gstCertificate: 'GST Certificate'
      };
      toast.success(`${docNames[docType] || docType} uploaded successfully!`);
    }
  };

  const handleDigiLockerFetch = async () => {
    if (isFetchingDigiLocker) return;

    // Check if we have basic registration info first
    if (!registrationData.fullName || !registrationData.mobile) {
      toast.error("Please fill in your Name and Mobile in Step 1 first.");
      setCurrentStep(1);
      return;
    }

    setIsFetchingDigiLocker(true);
    const loadingToast = toast.loading("Redirecting to DigiLocker Secure Login...");

    try {
      const response = await axios.post('/api/signzy/create-digilocker-session', {
        fullName: registrationData.fullName,
        mobile: registrationData.mobile
      });

      if (response.data.success && response.data.sessionUrl) {
        // Save the request ID to localStorage to fetch data upon return
        localStorage.setItem('signzy_request_id', response.data.requestId);

        // Save form data to session storage so we don't lose it on redirect
        localStorage.setItem('dsa_reg_data', JSON.stringify({ ...registrationData, profileImage: null })); // Files can't be stringified easily
        localStorage.setItem('dsa_basic_data', JSON.stringify(basicData));
        localStorage.setItem('dsa_bank_data', JSON.stringify(bankData));

        toast.dismiss(loadingToast);
        toast.loading("Redirecting to DigiLocker...");

        // Redirect to Signzy/DigiLocker OAuth page
        setTimeout(() => {
          window.location.href = response.data.sessionUrl;
        }, 1000);

      } else {
        throw new Error(response.data.message || 'Identity session creation failed');
      }
    } catch (error) {
      console.error("Signzy Session Error:", error.response?.data || error.message);
      setIsFetchingDigiLocker(false);
      toast.dismiss(loadingToast);

      const errorMessage = error.response?.data?.message || "Failed to create DigiLocker session";
      toast.error(errorMessage);
    }
  };

  // Download Summary as PDF
  const downloadSummary = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;

    // Header
    doc.setFillColor(40, 48, 121); // Primary color #283079
    doc.rect(0, 0, pageWidth, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.text("DSA PARTNER REGISTRATION", pageWidth / 2, 25, { align: 'center' });
    doc.setFontSize(10);
    doc.text("Aham Housing Finance Limited", pageWidth / 2, 33, { align: 'center' });

    let yPos = 50;

    // Application Info
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Application Status:", 15, yPos);
    doc.setFont("helvetica", "normal");
    doc.text("Submitted Successfully", 60, yPos);
    yPos += 7;
    doc.setFont("helvetica", "bold");
    doc.text("Date:", 15, yPos);
    doc.setFont("helvetica", "normal");
    doc.text(new Date().toLocaleString(), 60, yPos);
    yPos += 7;
    doc.setFont("helvetica", "bold");
    doc.text("Assigned Branch:", 15, yPos);
    doc.setFont("helvetica", "normal");
    doc.text(assignedBranch, 60, yPos);
    yPos += 15;

    // Section: Registration Details
    autoTable(doc, {
      startY: yPos,
      head: [['REGISTRATION DETAILS', '']],
      body: [
        ['Full Name', registrationData.fullName],
        ['Email', registrationData.email],
        ['Mobile', registrationData.mobile],
        ['PIN Code', registrationData.pinCode],
        ['City', registrationData.city],
        ['Business Type', registrationData.businessType],
        ['Address', registrationData.address]
      ],
      headStyles: { fillColor: [200, 200, 200], textColor: [0, 0, 0], fontStyle: 'bold' },
      theme: 'striped'
    });

    yPos = doc.lastAutoTable.finalY + 10;

    // Section: Basic Details
    autoTable(doc, {
      startY: yPos,
      head: [['BASIC DETAILS (KYC)', '']],
      body: [
        ['GST Certification', basicData.gstCertification || 'N/A'],
        ['PAN Number', basicData.panNumber],
        ['Full Name (as per PAN)', basicData.fullNameAsPan],
        ['Date of Birth', basicData.dateOfBirth],
         ['Aadhaar Number', maskAadhaar(basicData.aadhaarNumber)]
      ],
      headStyles: { fillColor: [200, 200, 200], textColor: [0, 0, 0], fontStyle: 'bold' },
      theme: 'striped'
    });

    yPos = doc.lastAutoTable.finalY + 10;

    // Section: Bank Details
    autoTable(doc, {
      startY: yPos,
      head: [['BANK DETAILS', '']],
      body: [
        ['Account Number', bankData.accountNumber],
        ['IFSC Code', bankData.ifscCode],
        ['Bank/Branch', bankData.bankBranch]
      ],
      headStyles: { fillColor: [200, 200, 200], textColor: [0, 0, 0], fontStyle: 'bold' },
      theme: 'striped'
    });

    yPos = doc.lastAutoTable.finalY + 10;

    // Documents check
    const docs = [
      ['Proof of Identity', documents.proofOfIdentity ? (documents.proofOfIdentity.name || 'Uploaded') : 'Not uploaded'],
      ['Proof of Address', documents.proofOfAddress ? (documents.proofOfAddress.name || 'Uploaded') : 'Not uploaded'],
      ['PAN Card', documents.panCard ? (documents.panCard.name || 'Uploaded') : 'Not uploaded'],
      ['Cancelled Cheque', documents.cancelledCheque ? (documents.cancelledCheque.name || 'Uploaded') : 'Not uploaded'],
      ['GST Certificate', documents.gstCertificate ? (documents.gstCertificate.name || 'Uploaded') : 'Not uploaded']
    ];

    autoTable(doc, {
      startY: yPos,
      head: [['DOCUMENTS STATUS', '']],
      body: docs,
      headStyles: { fillColor: [200, 200, 200], textColor: [0, 0, 0], fontStyle: 'bold' },
      theme: 'striped'
    });

    // Footer
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `© ${new Date().getFullYear()} Aham Housing Finance Limited. Confidential document. Page ${i} of ${totalPages}`,
        pageWidth / 2,
        doc.internal.pageSize.height - 10,
        { align: 'center' }
      );
    }

    doc.save(`Aham_Partner_Registration_${registrationData.fullName.replace(/\s+/g, '_')}.pdf`);
    toast.success('Summary PDF downloaded successfully!');
  };

  const handleFinalSubmit = async () => {
    const agreeCheckbox = document.getElementById('agreeTerms');
    if (agreeCheckbox && !agreeCheckbox.checked) {
      toast.error("Please read and agree to the DSA Agreement to proceed.");
      return;
    }

    setIsSubmitting(true);

    // DSA Code will now be generated upon Admin approval
    const generatedDsaCode = 'Pending';

    // Assign branch logic based on sourcing city
    let assignedBranchInfo = "";
    const sourcingCity = registrationData.city.trim().toLowerCase();

    // 1. Try to find direct exact match in availableBranches by city or name first
    let matchedBranch = availableBranches.find(b =>
      b.city?.toLowerCase() === sourcingCity ||
      b.name?.toLowerCase() === sourcingCity
    );

    // 2. If not found, use the fallback mapping and OpenStreetMap query logic
    if (!matchedBranch) {
      // Predefined nearby branch mapping for specific sourcing cities (Overrides)
      const cityOverrides = {
        'kallakurichi': 'attur',
        'ulundurpet': 'villupuram',
        'ulundurpettai': 'villupuram',
        'rasipuram': 'salem'
      };

      // TN Districts to Branch mapping (covers all of Tamil Nadu)
      const districtToBranchMap = {
        'ariyalur': 'trichy',
        'chengalpattu': 'chengalpet',
        'chennai': 'chennai-mount road',
        'coimbatore': 'coimbatore',
        'cuddalore': 'villupuram',
        'dharmapuri': 'salem',
        'dindigul': 'dindigul',
        'erode': 'erode',
        'kallakurichi': 'attur',
        'kanchipuram': 'kanchipuram',
        'kanyakumari': 'madurai',
        'karur': 'trichy',
        'krishnagiri': 'salem',
        'madurai': 'madurai',
        'mayiladuthurai': 'trichy',
        'nagapattinam': 'trichy',
        'namakkal': 'salem',
        'nilgiris': 'coimbatore',
        'perambalur': 'trichy',
        'pudukkottai': 'trichy',
        'ramanathapuram': 'madurai',
        'ranipet': 'vellore',
        'salem': 'salem',
        'sivaganga': 'madurai',
        'tenkasi': 'madurai',
        'thanjavur': 'trichy',
        'theni': 'usalampatti',
        'thoothukudi': 'madurai',
        'tiruchirappalli': 'trichy',
        'tirunelveli': 'madurai',
        'tirupathur': 'vellore',
        'tiruppur': 'coimbatore',
        'tiruvallur': 'tiruvallur',
        'tiruvannamalai': 'vellore',
        'tiruvarur': 'trichy',
        'vellore': 'vellore',
        'viluppuram': 'villupuram',
        'virudhunagar': 'madurai'
      };

      let targetBranchName = null;

      // Check exact city overrides first
      const mappedCityKey = Object.keys(cityOverrides).find(key => sourcingCity.includes(key) || key.includes(sourcingCity));
      if (mappedCityKey) {
        targetBranchName = cityOverrides[mappedCityKey];
      } else {
        // Fetch District from OpenStreetMap for the entered city
        try {
          const nomRes = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(sourcingCity)},Tamil+Nadu&format=json&addressdetails=1&limit=1`);
          const nomData = await nomRes.json();

          if (nomData && nomData.length > 0 && nomData[0].address) {
            const address = nomData[0].address;
            // Extract district name
            let district = (address.state_district || address.county || address.city || "").toLowerCase().replace(' district', '').trim();

            // Check if district is in our TN mapping
            const districtKey = Object.keys(districtToBranchMap).find(key => district.includes(key) || key.includes(district));
            if (districtKey) {
              targetBranchName = districtToBranchMap[districtKey];
            } else {
              targetBranchName = district; // Fallback to raw district name
            }
          }
        } catch (err) {
          console.error("Error fetching city details:", err);
        }
      }

      if (targetBranchName) {
        matchedBranch = availableBranches.find(b =>
          b.city?.toLowerCase().includes(targetBranchName) ||
          b.name?.toLowerCase().includes(targetBranchName)
        );
      }

      // Fallback to direct partial text match of sourcingCity
      if (!matchedBranch) {
        matchedBranch = availableBranches.find(b =>
          b.city?.toLowerCase().includes(sourcingCity) ||
          sourcingCity.includes(b.city?.toLowerCase()) ||
          b.name?.toLowerCase().includes(sourcingCity)
        );
      }
    }

    if (matchedBranch) {
      // Check if it's an exact match or a "nearby" assignment
      const isExact = matchedBranch.city?.toLowerCase() === sourcingCity || matchedBranch.name?.toLowerCase() === sourcingCity;
      if (isExact) {
        assignedBranchInfo = `${matchedBranch.name} — BM: ${matchedBranch.contact || '+91 78239 11555'}`;
      } else {
        assignedBranchInfo = `${matchedBranch.name} (Nearest Branch) — BM: ${matchedBranch.contact || '+91 78239 11555'}`;
      }
    } else {
      // Final Fallback to Chennai or first available
      const defaultBranch = availableBranches.find(b => b.city?.toLowerCase() === 'chennai') || availableBranches[0];
      if (defaultBranch) {
        assignedBranchInfo = `${defaultBranch.name} (Nearest Branch) — BM: ${defaultBranch.contact || '+91 78239 11555'}`;
      } else {
        assignedBranchInfo = `Chennai Branch — BM: +91 78239 11555`;
      }
    }

    // Create FormData for Multi-part upload (needed for actual files)
    const formData = new FormData();

    formData.append('assignedBranch', assignedBranchInfo);


    // Append Text Data as Strings
    formData.append('registrationData', JSON.stringify({
      ...registrationData,
      profileImageName: registrationData.profileImage?.name || 'Not uploaded'
    }));
    formData.append('basicData', JSON.stringify(basicData));
    formData.append('bankData', JSON.stringify(bankData));

    // Append Document Metadata
    formData.append('documents', JSON.stringify({
      proofOfIdentity: documents.proofOfIdentity instanceof File ? documents.proofOfIdentity.name : documents.proofOfIdentity || 'Not uploaded',
      proofOfAddress: documents.proofOfAddress instanceof File ? documents.proofOfAddress.name : documents.proofOfAddress || 'Not uploaded',
      panCard: documents.panCard instanceof File ? documents.panCard.name : documents.panCard || 'Not uploaded',
      cancelledCheque: documents.cancelledCheque instanceof File ? documents.cancelledCheque.name : documents.cancelledCheque || 'Not uploaded',
      photograph: documents.gstCertificate instanceof File ? documents.gstCertificate.name : documents.gstCertificate || 'Not uploaded'
    }));

    // APPEND ACTUAL FILES
    if (registrationData.profileImage instanceof File) {
      formData.append('profileImage', registrationData.profileImage);
    }

    if (documents.proofOfIdentity instanceof File) {
      formData.append('proofOfIdentity', documents.proofOfIdentity);
    }
    if (documents.proofOfAddress instanceof File) {
      formData.append('proofOfAddress', documents.proofOfAddress);
    }
    if (documents.panCard instanceof File) {
      formData.append('panCard', documents.panCard);
    }
    if (documents.cancelledCheque instanceof File) {
      formData.append('cancelledCheque', documents.cancelledCheque);
    }
    if (documents.gstCertificate instanceof File) {
      formData.append('photograph', documents.gstCertificate);
    }

    try {
      // Send via multipart/form-data
      const response = await fetch("/api/dsa-email/send-dsa-registration", {
        method: "POST",
        // Note: We don't set Content-Type header; browser does it automatically for FormData
        body: formData
      });

      const result = await response.json();

      if (result.success) {
        setDsaCode(generatedDsaCode);
        setAssignedBranch(assignedBranchInfo);
        setSubmissionSuccess(true);
        localStorage.removeItem('dsa_reg_data');
        localStorage.removeItem('dsa_basic_data');
        localStorage.removeItem('dsa_bank_data');
        localStorage.removeItem('dsa_profile_image_base64');
        localStorage.removeItem('dsa_profile_image_filename');
        toast.success("Registration completed & files uploaded successfully!");
      } else {
        setDsaCode(generatedDsaCode);
        setAssignedBranch(assignedBranchInfo);
        setSubmissionSuccess(true);
        localStorage.removeItem('dsa_reg_data');
        localStorage.removeItem('dsa_basic_data');
        localStorage.removeItem('dsa_bank_data');
        localStorage.removeItem('dsa_profile_image_base64');
        localStorage.removeItem('dsa_profile_image_filename');
        toast.info("Registration saved, but file upload failed.");
      }
    } catch (error) {
      console.error("Submission error:", error);
      setDsaCode(generatedDsaCode);
      setAssignedBranch(assignedBranchInfo);
      setSubmissionSuccess(true);
      toast.success("Application submitted!");
    } finally {
      setIsSubmitting(false);
    }
  };


  // Scroll to section helper
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth'
      });
    }
  };



  // Icon Mapping
  const iconMap = {
    BanknotesIcon,
    ClockIcon,
    HomeIcon,
    ShieldCheckIcon,
    UserGroupIcon,
    ChartBarIcon,
    CurrencyRupeeIcon,
    DocumentTextIcon,
    SparklesIcon,
    UserIcon,
    BuildingOfficeIcon,
    BriefcaseIcon,
    DocumentCheckIcon,
    AcademicCapIcon,
    CheckCircleIcon,
    LightBulbIcon,
    PresentationChartLineIcon
  };

  const renderIcon = (iconName, className = "h-6 w-6") => {
    const IconComponent = iconMap[iconName] || DocumentTextIcon;
    return <IconComponent className={className} />;
  };

  // Value Propositions (Fallback if API fails, though we use state)
  const defaultValuePropositions = [
    {
      icon: BanknotesIcon,
      title: 'Attractive Pay-outs & On-Time Payments',
      description: 'Earn competitive referral incentives with transparent and timely payouts on every disbursed loan.',
      gradient: 'from-green-500 to-emerald-600'
    },
    {
      icon: ClockIcon,
      title: 'Quick Loan Approvals',
      description: 'Our interview-based scorecard assessment helps evaluate customers with informal income, leading to faster approvals and higher conversions.',
      gradient: 'from-blue-500 to-cyan-600'
    },
    {
      icon: HomeIcon,
      title: 'High Demand Products',
      description: 'We specialize in affordable housing loans with flexible documentation options for various needs including purchase, construction, renovation, and more.',
      gradient: 'from-purple-500 to-pink-600'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Simple, Hassle-Free Process',
      description: 'Share leads easily through WhatsApp, phone, or simple online form. Track updates through our team.',
      gradient: 'from-orange-500 to-red-600'
    }
  ];

  const displayBenefits = benefits.length > 0 ? benefits.map(b => ({
    ...b,
    icon: iconMap[b.icon_name] || BanknotesIcon,
    gradient: `from-${b.gradient_from} to-${b.gradient_to}`
  })) : defaultValuePropositions;

  // High Demand Products - Dynamic state
  const [highDemandProducts, setHighDemandProducts] = useState([]);
  const [highDemandSettings, setHighDemandSettings] = useState(null);

  // Partner Types
  // Partner Types
  const [partnerTypes, setPartnerTypes] = useState([]);
  const [partnerTypesSettings, setPartnerTypesSettings] = useState(null);

  const defaultPartnerTypes = [
    { icon: UserGroupIcon, title: 'Direct Selling Agents (DSAs)', color: 'text-blue-600' },
    { icon: BuildingOfficeIcon, title: 'Real estate brokers / developers / builders', color: 'text-green-600' },
    { icon: ChartBarIcon, title: 'Financial consultants', color: 'text-purple-600' },
  ];

  const displayPartnerTypes = partnerTypes.length > 0 ? partnerTypes.map(t => ({
    ...t,
    icon: iconMap[t.icon_name] || UserGroupIcon,
    color: `text-${t.gradient_from}`
  })) : defaultPartnerTypes;

  // Partnership Steps
  // Partnership Steps
  const [partnershipSteps, setPartnershipSteps] = useState([]);
  const [partnershipStepsSettings, setPartnershipStepsSettings] = useState(null);
  const [supportCards, setSupportCards] = useState([]);
  const [supportSettings, setSupportSettings] = useState(null);
  const [developmentItems, setDevelopmentItems] = useState([]);
  const [developmentSettings, setDevelopmentSettings] = useState(null);
  const [successStories, setSuccessStories] = useState([]);
  const [successStoriesSettings, setSuccessStoriesSettings] = useState(null);
  const [registrationRequirements, setRegistrationRequirements] = useState([]);
  const [registrationRequirementsSettings, setRegistrationRequirementsSettings] = useState(null);

  const defaultPartnershipSteps = [
    {
      step: '1',
      title: 'Register as a Partner',
      description: 'Fill out the online form and our team will connect with you.',
      icon: DocumentTextIcon,
      color: 'from-blue-500 to-cyan-500'
    },
    {
      step: '2',
      title: 'Share Customer Leads',
      description: 'Provide basic customer details via WhatsApp, form, or phone.',
      icon: UserGroupIcon,
      color: 'from-purple-500 to-pink-500'
    },
    {
      step: '3',
      title: 'We Process the Loan Quickly',
      description: 'Our credit team connects with the customer, evaluates eligibility, and processes the loan.',
      icon: ChartBarIcon,
      color: 'from-orange-500 to-red-500'
    },
    {
      step: '4',
      title: 'You Earn Incentives',
      description: 'You get paid for every loan successfully disbursed. Fast, transparent, and reliable.',
      icon: CurrencyRupeeIcon,
      color: 'from-green-500 to-emerald-500'
    }
  ];

  const displayPartnershipSteps = partnershipSteps.length > 0 ? partnershipSteps.map((s, index) => ({
    ...s,
    step: (index + 1).toString(),
    icon: iconMap[s.icon_name] || DocumentTextIcon,
    color: `from-${s.gradient_from} to-${s.gradient_to}`
  })) : defaultPartnershipSteps;





  // Partner Resources
  const partnerResources = [
    { name: 'Product brochure', type: 'PDF', size: '2.5 MB' },
    // { name: 'Partner agreement form', type: 'PDF', size: '1.2 MB' }
  ];

  // FAQs - Placeholder
  const defaultFaqs = [
    {
      question: 'What is the DSA partner program?',
      answer: 'The DSA (Direct Selling Agent) partner program allows individuals and organizations to earn attractive incentives by referring potential home loan customers to Aham Housing Finance. You act as a bridge between customers and our services.'
    },
    {
      question: 'How much can I earn as a partner?',
      answer: 'Your earnings depend on the number and value of loans disbursed through your referrals. We offer competitive pay-out structures with transparent terms. Contact our partnership manager for detailed incentive information.'
    },
    {
      question: 'Is there any registration fee?',
      answer: 'No, there is absolutely no registration fee to become an Aham HFC partner. The onboarding process is completely free of cost.'
    },
    {
      question: 'How do I track my referrals?',
      answer: 'Once you refer a customer, our dedicated relationship manager will keep you updated on the loan application status. You can also reach out to your assigned partner support contact anytime.'
    },
    {
      question: 'What kind of support and training do you provide?',
      answer: 'We provide comprehensive product training, marketing materials, dedicated relationship managers, and ongoing support to help you succeed as a partner. Regular updates on products and policies are also shared.'
    },
    {
      question: 'In which locations does Aham HFC operate?',
      answer: 'Aham Housing Finance currently serves customers across Tamil Nadu, Karnataka, Telangana, and Andhra Pradesh. We are continuously expanding our reach to serve more customers.'
    }
  ];

  const displayFaqs = faqs && faqs.length > 0 ? faqs : defaultFaqs;

  return (
    <div className="min-h-screen bg-white">
      {/* Toast Notifications */}

      <SEO
        title="Home Loan DSA Partner Program: High Payouts | Aham Housing Finance"
        description="Join the Aham Housing Finance DSA Partner Program with zero investment. Get the highest home loan commissions, real-time lead tracking, and expert training. Register now!"
        keywords="Aham Housing Finance DSA partner, Aham DSA partner program, home loan DSA partner, home loan agent registration, loan DSA registration, direct selling agent home loan, housing finance partner program, affordable housing loan partner, home loan referral partner, mortgage DSA partner, loan channel partner, home loan distributor, DSA for housing finance, Aham home loan partner, become a DSA partner, home loan lead partner, loan sourcing partner, housing loan agent, DSA registration online, affordable home loan DSA"
        canonical="https://ahamhfc.com/become-home-loan-dsa-partner"
        ogTitle="Home Loan DSA Partner Program: High Payouts | Aham Housing Finance"
        ogDescription="Join the Aham Housing Finance DSA Partner Program with zero investment. Get the highest home loan commissions, real-time lead tracking, and expert training. Register now!"
      />

      {/* Hero Banner Section */}
      <section className="relative min-h-[60vh] flex items-center justify-start overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('${banner?.image_url ? `/${banner.image_url}` : '/img/ahamlife/Partner-Banner.webp'}')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/80 to-transparent"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 py-12 md:py-20 text-left w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h1 className="text-3xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
              {banner?.title || 'Partner With Aham Housing Finance'}
            </h1>
            <p className="text-lg md:text-2xl text-white/90 font-medium mb-6 md:mb-8 leading-relaxed max-w-2xl">
              {banner?.subtitle || 'Grow your income by helping families achieve their dream of home ownership'}
            </p>

            {/* Breadcrumb Navigation */}
            <div className="flex items-center space-x-2">
              <Link
                to="/"
                className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium"
              >
                Home
              </Link>
              <ChevronRightIcon className="h-4 w-4 text-white/60" />
              <span className="text-yellow-300 text-sm font-semibold">Partner Us</span>
            </div>

          </motion.div>
        </div>
      </section>

      {/* Sticky Navigation */}
      {/* <div className="sticky top-0 z-50 bg-white shadow-lg border-b border-gray-200"> */}
      <div className="sticky top-[80px] z-50 bg-white shadow-lg border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex overflow-x-auto scrollbar-hide py-4 gap-2 md:justify-center">
            {[
              { id: 'why-partner', label: 'Why Partner' },
              { id: 'who-can-join', label: 'Who Can Join' },
              { id: 'how-it-works', label: 'How It Works' },
              { id: 'support-training', label: 'Support & Training' },
              { id: 'success-stories', label: 'Success Stories' },
              // { id: 'resources', label: 'Resources' },
              { id: 'requirements', label: 'Requirements' },
              { id: 'dsa-registration', label: 'Register' },
              { id: 'faq', label: 'FAQ' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => scrollToSection(tab.id)}
                className={`whitespace-nowrap px-6 py-2 rounded-full font-semibold text-xs transition-all ${activeSection === tab.id
                  ? "bg-yellow-400 text-blue-800 shadow-lg"
                  : "text-gray-600 hover:bg-primary-100 hover:text-primary-700"
                  }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Introduction Section */}
      {(overview?.is_active !== false) && (
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto text-center"
            >
              <p className="text-xl text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{
                __html: overview?.description || `Aham Housing Finance invites channel partners or DSAs who can be real-estate agents, community leaders,
                and individuals with strong local networks to join our partnership program. Refer customers looking for
                <span class="font-semibold text-primary-600"> affordable home loans up to ₹50 lakhs</span>—even without formal income proofs—and
                earn attractive incentives on every successful loan disbursement.`
              }} />
            </motion.div>
          </div>
        </section>
      )}

      {/* Sub Menu 1: Why Partner with Us */}
      <section id="why-partner" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
              {benefitsSettings?.section_title || 'Why Partner with Us?'}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {benefitsSettings?.section_subtitle || 'Strong value propositions that help you grow faster'}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {displayBenefits.map((prop, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group bg-white rounded-2xl p-8 shadow-soft hover:shadow-large transition-all duration-300 border border-gray-100 text-center"
              >
                <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${prop.gradient} flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform duration-300`}>
                  <prop.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {prop.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {prop.description}
                </p>
              </motion.div>
            ))}
          </div>

          {/* High Demand Products */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mt-16 max-w-5xl mx-auto bg-gradient-to-br from-primary-50 to-blue-50 rounded-3xl p-10"
          >
            <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              {highDemandSettings?.section_title || 'High Demand Products'}
            </h3>
            <p className="text-lg text-gray-700 mb-6 text-center">
              {highDemandSettings?.section_subtitle || 'We specialize in affordable housing loans for:'}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {highDemandProducts.length > 0 ? (
                highDemandProducts.map((product, index) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    viewport={{ once: true }}
                    className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-soft"
                  >
                    <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0" />
                    <span className="text-gray-800 font-medium">{product.product_name}</span>
                  </motion.div>
                ))
              ) : (
                <div className="col-span-2 text-center text-gray-500">
                  Loading products...
                </div>
              )}
            </div>
            <p className="text-sm text-gray-600 mt-6 text-center italic">
              All with flexible documentation options
            </p>
          </motion.div>

          {/* Additional Benefits */}
          {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto mt-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border border-green-200"
            >
              <div className="w-14 h-14 rounded-xl bg-green-600 flex items-center justify-center mb-4">
                <SparklesIcon className="h-7 w-7 text-white" />
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                Simple, Hassle-Free Process
              </h4>
              <p className="text-gray-700">
                Share leads easily through WhatsApp, phone, or simple online form. Track updates through our team.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 border border-blue-200"
            >
              <div className="w-14 h-14 rounded-xl bg-blue-600 flex items-center justify-center mb-4">
                <AcademicCapIcon className="h-7 w-7 text-white" />
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                Dedicated Partner Support
              </h4>
              <p className="text-gray-700">
                You get access to relationship managers, product training, and constant guidance throughout the journey.
              </p>
            </motion.div>
          </div> */}
        </div>
      </section>

      {/* Sub Menu 2: Who Can Become a Partner */}
      <section id="who-can-join" className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
              {partnerTypesSettings?.section_title || 'Who Can Become a Partner?'}
            </h2>
            <p className="text-xl text-gray-600">
              {partnerTypesSettings?.section_subtitle || 'Anyone with a good customer network can join!'}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {displayPartnerTypes.map((type, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="group bg-white rounded-xl p-6 shadow-soft hover:shadow-large transition-all duration-300 border border-gray-100 hover:border-primary-200 text-center"
              >
                <div className="flex items-center justify-center mx-auto mb-4 w-14 h-14">
                  <type.icon className={`h-12 w-12 ${type.color} group-hover:scale-110 transition-transform duration-300`} />
                </div>
                <h3 className="text-lg font-semibold text-gray-800">
                  {type.title}
                </h3>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <div className="inline-block bg-gradient-to-r from-primary-600 to-blue-600 text-white px-8 py-4 rounded-2xl shadow-lg">
              <p className="text-lg font-semibold">
                ✨ {partnerTypesSettings?.cta_text || 'No registration fee. No prior financial experience needed.'}
              </p>
            </div>
          </motion.div>

          {(partnerTypesSettings?.bottom_text || partnerTypesSettings?.bottom_text === undefined) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="mt-8 text-center"
            >
              <p
                onClick={handleRichTextClick}
                className="text-base md:text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed [&_a]:text-primary-600 [&_a]:underline hover:[&_a]:text-primary-700 [&_a]:font-semibold [&_a]:transition-colors cursor-pointer"
                dangerouslySetInnerHTML={{
                  __html: sanitizeAndFormatHtml(
                    partnerTypesSettings?.bottom_text ||
                    'For more information about Aham Housing Finance’s <a href="https://ahamhfc.com/aham-housing-finance-partnership-channel-partner">partnership and channel partner opportunities</a>, explore our partnership page.'
                  )
                }}
              />
            </motion.div>
          )}
        </div>
      </section>

      {/* Sub Menu 3: How Partnership Works */}
      <section id="how-it-works" className="py-20 bg-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary-100/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-100/30 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
              {partnershipStepsSettings?.section_title || 'How Partnership Works'}
            </h2>
            <p className="text-xl text-gray-600">
              {partnershipStepsSettings?.section_subtitle || 'A simple 4-step process designed for convenience'}
            </p>
          </motion.div>

          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {displayPartnershipSteps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="relative"
                >
                  <div className="bg-white rounded-2xl p-8 shadow-large hover:shadow-colored transition-all duration-300 border border-gray-100 h-full text-center">
                    {/* Step Number */}
                    <div className={`absolute -top-4 -left-4 w-12 h-12 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center text-white font-bold text-xl shadow-lg`}>
                      {step.step}
                    </div>

                    {/* Icon */}
                    <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center mb-6 mx-auto`}>
                      <step.icon className="h-8 w-8 text-white" />
                    </div>

                    {/* Content */}
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {step.description}
                    </p>
                  </div>

                  {/* Arrow for desktop */}
                  {index < displayPartnershipSteps.length - 1 && index % 2 === 0 && (
                    <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-20">
                      <ArrowRightIcon className="h-8 w-8 text-primary-400" />
                    </div>
                  )}
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="mt-12 text-center"
            >
              <p className="text-2xl font-semibold text-primary-600">
                {partnershipStepsSettings?.cta_text || 'Fast, transparent, and reliable.'}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Sub Menu 4: Support & Training */}
      <section id="support-training" className="py-20 bg-gradient-to-b from-primary-50 to-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
              {supportSettings?.section_title || 'Support & Training'}
            </h2>
            <p className="text-xl text-gray-600">
              {supportSettings?.section_subtitle || 'Comprehensive resources to help you succeed'}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {supportCards.map((card, index) => (
              <motion.div
                key={card.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-8 shadow-large hover:shadow-colored transition-all duration-300 text-center"
              >
                <div className={`w-16 h-16 rounded-xl bg-gradient-to-br from-${card.gradient_from} to-${card.gradient_to} flex items-center justify-center mb-6 mx-auto`}>
                  {renderIcon(card.icon_name, "h-8 w-8 text-white")}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {card.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {card.description}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mt-12 max-w-4xl mx-auto bg-white rounded-2xl p-8 shadow-large"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
              {developmentSettings?.section_title || 'Ongoing Partner Development'}
            </h3>
            <ul className="space-y-3">
              {developmentItems.map((item) => (
                <li key={item.id} className="flex items-start gap-3">
                  {renderIcon(item.icon_name, "h-6 w-6 text-primary-600 flex-shrink-0 mt-1")}
                  <span className="text-gray-700">{item.title}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Sub Menu 5: Partner Success Stories */}
      < section id="success-stories" className="py-20 bg-gray-50" >
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
              {successStoriesSettings?.section_title || 'Partner Success Stories'}
            </h2>
            <p className="text-xl text-gray-600">
              {successStoriesSettings?.section_subtitle || 'Hear from our successful partners'}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {successStories.map((story, index) => (
              <motion.div
                key={story.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-8 shadow-large hover:shadow-colored transition-all duration-300"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(parseInt(story.gradient_to) || 5)].map((_, i) => (
                    <svg key={i} className="h-5 w-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-700 italic leading-relaxed mb-6">
                  "{story.description}"
                </p>
                <div className="border-t border-gray-200 pt-4">
                  <p className="text-gray-900 font-bold">— {story.title}</p>
                  <p className="text-gray-600 text-sm">{story.icon_name} ({story.gradient_from})</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section >

      {/* Sub Menu 6: Partner Resources */}
      {/* < section id="resources" className="py-20 bg-white" >
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
              Partner Resources
            </h2>
            <p className="text-xl text-gray-600">
              Download essential materials to get started
            </p>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-6">
            {partnerResources.map((resource, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-r from-gray-50 to-white rounded-xl p-6 shadow-soft hover:shadow-large transition-all duration-300 border border-gray-200 flex items-center justify-between group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center">
                    <DocumentTextIcon className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 group-hover:text-primary-600 transition-colors">
                      {resource.name}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {resource.type} • {resource.size}
                    </p>
                  </div>
                </div>
                <button className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold transition-all duration-300 flex items-center gap-2">
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section > */}

      {/* Sub Menu 7: Partner Registration Requirements */}
      < section id="requirements" className="py-20 bg-gradient-to-b from-primary-50 to-white" >
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
              {registrationRequirementsSettings?.section_title || 'Partner Registration Requirements'}
            </h2>
            <p className="text-xl text-gray-600">
              {registrationRequirementsSettings?.section_subtitle || 'Simple documentation to get you started'}
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl p-10 shadow-large"
            >
              <p className="text-lg text-gray-700 mb-8 text-center">
                To onboard as an official partner, you simply need:
              </p>
              <div className="space-y-6">
                {registrationRequirements.map((req, index) => (
                  <motion.div
                    key={req.id}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-center gap-4 bg-gradient-to-r from-primary-50 to-blue-50 p-6 rounded-xl"
                  >
                    <div className="w-12 h-12 rounded-xl bg-primary-600 flex items-center justify-center flex-shrink-0">
                      {renderIcon(req.icon_name, "h-6 w-6 text-white")}
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-gray-900">{req.title}</h4>
                      {req.description && (
                        <p className="text-gray-600 text-sm mt-1">{req.description}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
              <div className="mt-10 text-center">
                <p className="text-lg font-semibold text-green-600">
                  ✓ Onboarding charges not applicable. No complex documentation.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section >

      {/* Sub Menu 8: Become a Partner (CTA Section) */}
      {/* <section id="become-partner" className=" bg-gray-50">
        <div className="container mx-auto px-4 grid grid-cols-1 lg:grid-cols-3 gap-10">

        
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="lg:col-span-2 bg-white shadow-lg rounded-2xl p-8 border border-gray-200"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary-900 mb-5">
              Partner with Aham Housing Finance
            </h2>

            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              Join our DSA network — fast digital onboarding, automated KYC verification,
              paperless e-sign and instant notifications.
            </p>

           
            <div className="mb-4">
              <p className="font-bold text-gray-800">Who can apply:</p>
              <p className="text-gray-700">
                Individuals, Proprietorships, Partnerships, Pvt. Ltd companies
              </p>
            </div>

          
            <div className="mb-4">
              <p className="font-bold text-gray-800">Documents:</p>
              <p className="text-gray-700">
                PAN, Aadhaar, Proof of Address, Cancelled Cheque, Photograph
              </p>
            </div>

          
            <div className="mb-6">
              <p className="font-bold text-gray-800">Process:</p>
              <p className="text-gray-700">
                Register → Verify OTP → Basic & Bank details → Upload Documents → eSign Agreement
              </p>
            </div>

           
            <div className="flex flex-wrap gap-4 mt-6">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => scrollToSection('dsa-registration')}
                className="bg-red-600 hover:bg-red-700 text-white px-7 py-3 rounded-lg font-semibold shadow-md"
              >
                Register Now
              </motion.button>
            </div>
          </motion.div>

         
          <div className="flex flex-col gap-6">

          
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white shadow-lg border border-gray-200 rounded-xl p-6"
            >
              <h3 className="text-lg font-bold text-gray-900 mb-2">Quick Start</h3>
              <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                Begin your digital onboarding flow. Verify identity via DigiLocker for faster processing.
              </p>

              <button 
                onClick={() => scrollToSection('dsa-registration')}
                className="bg-primary-600 hover:bg-primary-700 text-white px-5 py-2 rounded-lg font-semibold w-full transition-colors"
              >
                Start Registration
              </button>
            </motion.div>

           
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-white shadow-lg border border-gray-200 rounded-xl p-6"
            >
              <h3 className="text-lg font-bold text-gray-900 mb-2">Direct Support</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Need help with registration? Contact our partner support team at 
                <span className="font-bold text-primary-600 ml-1">info@ahamhfc.com</span>
              </p>
            </motion.div>

          </div>
        </div>
      </section> */}


      <section id="dsa-registration" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 max-w-5xl">

          {/* Step Navigation Tabs */}
          <div className="flex flex-wrap gap-3 mb-8 justify-center">
            {["Registration", "Basic", "Bank", "Documents", "Agreement"].map((tab, index) => (
              <button
                key={tab}
                disabled={!otpVerified && index > 0}
                className={`
                  px-6 py-2 rounded-full font-semibold text-sm transition-all
                  ${currentStep === index
                    ? "bg-primary-600 text-white shadow"
                    : currentStep > index
                      ? "bg-green-100 text-green-700 border border-green-300"
                      : "bg-white border text-gray-400"
                  } 
                  ${!otpVerified && index > 0 ? "cursor-not-allowed opacity-50" : ""}
                `}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* STEP 1: Registration & OTP Verification */}
          {
            currentStep === 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white shadow-lg rounded-2xl border border-gray-200 p-8"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Step 1 — Registration
                </h3>
                <p className="text-gray-600 mb-6">Please provide your contact details for verification</p>

                <form className="grid grid-cols-1 md:grid-cols-2 gap-6" onSubmit={otpSent ? handleVerifyOtp : handleSendOtp}>

                  {/* Full Name */}
                  <div className="flex flex-col">
                    <label className="font-semibold text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      name="fullName"
                      placeholder="Your full name"
                      value={registrationData.fullName}
                      onChange={handleRegistrationChange}
                      required
                      className="border rounded-lg px-4 py-3 focus:ring-primary-500 focus:outline-none"
                      disabled={otpSent}
                    />
                  </div>

                  {/* Email */}
                  <div className="flex flex-col">
                    <label className="font-semibold text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                    <input
                      type="email"
                      name="email"
                      placeholder="you@example.com"
                      value={registrationData.email}
                      onChange={handleRegistrationChange}
                      required
                      className="border rounded-lg px-4 py-3 focus:ring-primary-500 focus:outline-none"
                      disabled={otpSent}
                    />
                  </div>

                  {/* Mobile Number */}
                  <div className="flex flex-col">
                    <label className="font-semibold text-gray-700 mb-1">Mobile Number <span className="text-red-500">*</span></label>
                    <input
                      type="tel"
                      name="mobile"
                      placeholder="10-digit mobile"
                      value={registrationData.mobile}
                      onChange={handleRegistrationChange}
                      maxLength={10}
                      required
                      className="border rounded-lg px-4 py-3 focus:ring-primary-500 focus:outline-none"
                      disabled={otpSent}
                    />
                  </div>

                  {/* PIN Code */}
                  <div className="flex flex-col">
                    <label className="font-semibold text-gray-700 mb-1">PIN Code <span className="text-red-500">*</span></label>
                    <input
                      type="number"
                      name="pinCode"
                      placeholder="560001"
                      value={registrationData.pinCode}
                      onChange={handleRegistrationChange}
                      required
                      className="border rounded-lg px-4 py-3 focus:ring-primary-500 focus:outline-none"
                      disabled={otpSent}
                    />
                  </div>

                  {/* Sourcing City */}
                  <div className="flex flex-col">
                    <label className="font-semibold text-gray-700 mb-1">Sourcing City <span className="text-red-500">*</span></label>
                    <select
                      name="city"
                      value={registrationData.city}
                      onChange={handleRegistrationChange}
                      required
                      className="border rounded-lg px-4 py-3 focus:ring-primary-500 focus:outline-none bg-white"
                      disabled={otpSent}
                    >
                      <option value="">Select Sourcing City</option>
                      {uniqueCities.map(city => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                  </div>

                  {/* Business Type */}
                  <div className="flex flex-col">
                    <label className="font-semibold text-gray-700 mb-1">Business Type <span className="text-red-500">*</span></label>
                    <select
                      name="businessType"
                      value={registrationData.businessType}
                      onChange={handleRegistrationChange}
                      className="border rounded-lg px-4 py-3 focus:ring-primary-500 focus:outline-none"
                      disabled={otpSent}
                    >
                      <option>Individual</option>
                      <option>Proprietorship</option>
                      <option>Partnership Firm</option>
                      <option>Public Limited Company</option>
                      <option>Pvt. Ltd Company</option>
                    </select>
                  </div>

                  {/* Address */}
                  <div className="flex flex-col">
                    <label className="font-semibold text-gray-700 mb-1">Address <span className="text-red-500">*</span></label>
                    <textarea
                      name="address"
                      placeholder="Enter your full address"
                      value={registrationData.address}
                      onChange={handleRegistrationChange}
                      required
                      rows={3}
                      className="border rounded-lg px-4 py-3 focus:ring-primary-500 focus:outline-none resize-none h-full"
                      disabled={otpSent}
                    />
                  </div>

                  {/* Profile Image Upload */}
                  <div className="flex flex-col">
                    <label className="font-semibold text-gray-700 mb-1">Upload Profile Image <span className="text-red-500">*</span></label>
                    <div className="border rounded-lg px-4 py-3 focus:ring-primary-500 focus:outline-none flex flex-col justify-center h-full bg-gray-50">
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            if (!['image/jpeg', 'image/png'].includes(file.type)) {
                              toast.error("Please upload only JPG or PNG images.");
                              e.target.value = null;
                              setRegistrationData(prev => ({ ...prev, profileImage: null }));
                              localStorage.removeItem('dsa_profile_image_base64');
                              localStorage.removeItem('dsa_profile_image_filename');
                              return;
                            }
                            setRegistrationData(prev => ({ ...prev, profileImage: file }));
                            
                            // Convert and save profile image as base64 to survive redirects
                            const reader = new FileReader();
                            reader.onload = (event) => {
                              try {
                                localStorage.setItem('dsa_profile_image_base64', event.target.result);
                                localStorage.setItem('dsa_profile_image_filename', file.name);
                              } catch (err) {
                                console.error("Error saving profile image base64:", err);
                              }
                            };
                            reader.readAsDataURL(file);

                            toast.success("Profile image selected!");
                          }
                        }}
                        className="text-sm"
                        disabled={otpSent}
                      />
                      {registrationData.profileImage && (
                        <div className="flex items-center gap-2 text-green-600 mt-2">
                          <CheckCircleIcon className="h-5 w-5" />
                          <span className="text-sm font-medium truncate">{registrationData.profileImage.name}</span>
                        </div>
                      )}
                      <p className="text-xs text-gray-500 mt-2">JPG, PNG - Max 2MB</p>
                    </div>
                  </div>


                  {/* OTP Fields - Visible only after Send OTP */}
                  {otpSent && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="md:col-span-2 p-6 bg-primary-50 rounded-xl border border-primary-100"
                    >
                      <div className="flex flex-col max-w-xs mx-auto text-center">
                        <label className="font-semibold text-primary-900 mb-3 text-lg">Enter 6-digit OTP</label>
                        <input
                          type="text"
                          placeholder="000000"
                          value={otp}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '');
                            if (val.length <= 6) setOtp(val);
                          }}
                          maxLength={6}
                          className="border-2 rounded-xl px-4 py-4 text-center text-3xl font-bold tracking-[0.5em] focus:ring-primary-500 focus:border-primary-500 focus:outline-none border-primary-200 shadow-inner"
                        />
                        <div className="mt-4 flex flex-col gap-2">
                          {resendTimer > 0 ? (
                            <p className="text-sm text-gray-500">
                              Resend OTP in <span className="font-bold text-primary-600">{resendTimer}s</span>
                            </p>
                          ) : (
                            <button
                              type="button"
                              onClick={handleSendOtp}
                              className="text-primary-600 font-bold hover:text-primary-800 text-sm flex items-center justify-center gap-1 mx-auto"
                            >
                              <SparklesIcon className="h-4 w-4" />
                              Resend OTP
                            </button>
                          )}
                          <p className="text-xs text-primary-700">
                            Sent to: <span className="font-semibold">{registrationData.mobile}</span>
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Button Row */}
                  <div className="md:col-span-2 flex mt-6 gap-4">
                    <button
                      type="submit"
                      disabled={isSubmitting || isSendingOtp}
                      className={`px-8 py-4 rounded-xl font-bold text-white transition-all duration-300 shadow-md ${isSubmitting || isSendingOtp ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-700 active:scale-95'}`}
                    >
                      {isSendingOtp ? 'Sending...' : (isSubmitting ? 'Verifying...' : (otpSent ? 'Verify & Continue' : 'Send Verification OTP'))}
                    </button>

                    {otpSent && !isSubmitting && !isSendingOtp && (
                      <button
                        type="button"
                        onClick={() => {
                          setOtpSent(false);
                          setOtp('');
                        }}
                        className="px-8 py-4 rounded-xl font-bold text-gray-600 border-2 border-gray-200 hover:bg-gray-50 bg-white transition-all duration-300 active:scale-95"
                      >
                        Edit Mobile
                      </button>
                    )}
                  </div>
                </form>
              </motion.div>
            )
          }

          {/* STEP 2: Basic Details */}
          {
            currentStep === 1 && otpVerified && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white shadow-lg rounded-2xl border border-gray-200 p-8"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Step 2 — Basic Details
                </h3>
                <p className="text-gray-600 mb-6">Please provide your KYC details</p>

                <form className="grid grid-cols-1 md:grid-cols-2 gap-6" onSubmit={handleBasicDetailsSubmit}>

                  {/* GST Certification */}
                  <div className="flex flex-col">
                    <label className="font-semibold text-gray-700 mb-1">GST Certification </label>
                    <input
                      type="text"
                      name="gstCertification"
                      value={basicData.gstCertification}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase();
                        // Only allow alphanumeric
                        if (/^[A-Z0-9]*$/.test(value)) {
                          handleBasicChange({ target: { name: 'gstCertification', value } });
                        }
                      }}
                      maxLength={15}

                      className="border rounded-lg px-4 py-3 focus:ring-primary-500 focus:outline-none uppercase"
                      placeholder="Enter 15-character GST (Optional)"
                    />
                  </div>

                  {/* PAN Number */}
                  <div className="flex flex-col">
                    <label className="font-semibold text-gray-700 mb-1">PAN Number <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      name="panNumber"
                      placeholder="ABCD12345E"
                      value={basicData.panNumber}
                      onChange={handleBasicChange}
                      maxLength={10}
                      required
                      className="border rounded-lg px-4 py-3 focus:ring-primary-500 focus:outline-none uppercase"
                    />
                  </div>

                  {/* Full Name (as per PAN) */}
                  <div className="flex flex-col">
                    <label className="font-semibold text-gray-700 mb-1">Full Name (as per PAN) <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      name="fullNameAsPan"
                      placeholder="Your full name"
                      value={basicData.fullNameAsPan}
                      onChange={handleBasicChange}
                      required
                      className="border rounded-lg px-4 py-3 focus:ring-primary-500 focus:outline-none"
                    />
                  </div>

                  {/* Date of Birth */}
                  <div className="flex flex-col">
                    <label className="font-semibold text-gray-700 mb-1">Date of Birth <span className="text-red-500">*</span></label>
                    <input
                      type="date"
                      name="dateOfBirth"
                      placeholder="dd/mm/yyyy"
                      value={basicData.dateOfBirth}
                      onChange={handleBasicChange}
                      required
                      className="border rounded-lg px-4 py-3 focus:ring-primary-500 focus:outline-none"
                      max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                    />
                  </div>

                  {/* Aadhaar Number */}
                  <div className="flex flex-col md:col-span-2">
                    <label className="font-semibold text-gray-700 mb-1">Aadhaar Number <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      name="aadhaarNumber"
                      placeholder="XXXX XXXX XXXX"
                      value={basicData.aadhaarNumber}
                      onChange={(e) => {
                        let value = e.target.value.replace(/\s/g, '').replace(/\D/g, '');
                        if (value.length <= 12) {
                          value = value.match(/.{1,4}/g)?.join(' ') || value;
                          handleBasicChange({ target: { name: 'aadhaarNumber', value } });
                        }
                      }}
                      maxLength={14}
                      required
                      className="border rounded-lg px-4 py-3 focus:ring-primary-500 focus:outline-none"
                    />
                  </div>

                  {/* Button Row */}
                  <div className="md:col-span-2 flex justify-between mt-6">
                    <button
                      type="button"
                      onClick={() => {
                        setCurrentStep(0);
                        setOtpVerified(false);
                        setOtpSent(false);
                      }}
                      className="px-8 py-3 rounded-lg font-semibold text-gray-600 border border-gray-300 hover:bg-gray-50 bg-white"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      className="px-8 py-3 rounded-lg font-semibold text-white bg-primary-600 hover:bg-primary-700 transition-all duration-300"
                    >
                      Next: Bank Details
                    </button>
                  </div>
                </form>
              </motion.div>
            )
          }

          {/* STEP 3: Bank Details */}
          {
            currentStep === 2 && otpVerified && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white shadow-lg rounded-2xl border border-gray-200 p-8"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Step 3 — Bank Details
                </h3>
                <p className="text-gray-600 mb-6">Please provide your bank account details for payouts</p>

                <form className="grid grid-cols-1 md:grid-cols-2 gap-6" onSubmit={handleBankDetailsSubmit}>

                  {/* Account Number */}
                  <div className="flex flex-col">
                    <label className="font-semibold text-gray-700 mb-1">Account Number <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      name="accountNumber"
                      placeholder="Enter account number"
                      value={bankData.accountNumber}
                      onChange={handleBankChange}
                      required
                      className="border rounded-lg px-4 py-3 focus:ring-primary-500 focus:outline-none"
                    />
                  </div>

                  {/* Re-enter Account Number */}
                  <div className="flex flex-col">
                    <label className="font-semibold text-gray-700 mb-1">Re-enter Account Number <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      name="reEnterAccountNumber"
                      placeholder="Re-enter account number"
                      value={bankData.reEnterAccountNumber}
                      onChange={handleBankChange}
                      required
                      className="border rounded-lg px-4 py-3 focus:ring-primary-500 focus:outline-none"
                    />
                  </div>

                  {/* IFSC Code */}
                  <div className="flex flex-col">
                    <label className="font-semibold text-gray-700 mb-1">IFSC Code <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      name="ifscCode"
                      placeholder="HDFC0001234"
                      value={bankData.ifscCode}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase();
                        handleBankChange({ target: { name: 'ifscCode', value } });
                        // Auto-lookup when IFSC is complete
                        if (value.length === 11) {
                          handleIfscLookup(value);
                        } else if (value.length < 11) {
                          setBankData(prev => ({ ...prev, bankBranch: '' }));
                          setIfscError('');
                        }
                      }}
                      maxLength={11}
                      required
                      className={`border rounded-lg px-4 py-3 focus:outline-none uppercase ${ifscError ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary-500 focus:ring-1 focus:border-primary-500'
                        }`}
                    />
                    {ifscError && (
                      <span className="text-red-500 text-xs mt-1 font-medium">{ifscError}</span>
                    )}
                  </div>

                  {/* Bank/Branch (Auto-filled) */}
                  <div className="flex flex-col">
                    <label className="font-semibold text-gray-700 mb-1">Bank/Branch (auto) <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      name="bankBranch"
                      placeholder="Will auto-fill from IFSC"
                      value={bankData.bankBranch}
                      readOnly
                      className="border rounded-lg px-4 py-3 bg-gray-50 text-gray-700 cursor-not-allowed"
                    />
                  </div>

                  {/* Button Row */}
                  <div className="md:col-span-2 flex justify-between mt-6">
                    <button
                      type="button"
                      onClick={() => {
                        setCurrentStep(1);
                      }}
                      className="px-8 py-3 rounded-lg font-semibold text-gray-600 border border-gray-300 hover:bg-gray-50 bg-white"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      className="px-8 py-3 rounded-lg font-semibold text-white transition-all duration-300 bg-primary-600 hover:bg-primary-700"
                    >
                      Next: Upload Documents
                    </button>
                  </div>
                </form>
              </motion.div>
            )
          }

          {/* STEP 4: Documents Upload */}
          {
            currentStep === 3 && otpVerified && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white shadow-lg rounded-2xl border border-gray-200 p-8"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Step 4 — Upload Documents
                </h3>
                {/* <p className="text-gray-600 mb-4">
                Recommended: Use DigiLocker (fast & secure). Or
                upload manually.
              </p> */}

                {/* DigiLocker / Manual Upload Toggle */}
                <div className="flex gap-3 mb-6">
                  <button
                    type="button"
                    onClick={handleDigiLockerFetch}
                    disabled={isFetchingDigiLocker}
                    className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${uploadMode === 'digilocker'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                  >

                    {isFetchingDigiLocker ? 'Connecting...' : 'Connect DigiLocker'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setUploadMode('manual')}
                    className={`px-6 py-3 rounded-lg font-semibold transition-all ${uploadMode === 'manual'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                  >
                    Manual Upload
                  </button>
                </div>

                {/* Note about file requirements */}
                <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg text-sm mb-6 flex items-center gap-2">
                  <svg className="w-5 h-5 text-yellow-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span><strong>Note:</strong> Only PDF files are allowed. Maximum file size: 2MB.</span>
                </div>

                <form className="space-y-6" onSubmit={(e) => {
                  e.preventDefault();
                  if (!documents.cancelledCheque) {
                    return toast.error("Cancelled Cheque is mandatory.");
                  }
                  setCurrentStep(4);
                  toast.success("Documents uploaded successfully!");
                }}>

                  {/* Document Upload Cards - Horizontal Layout */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">

                    {/* Proof of Identity */}
                    <div className="flex flex-col">
                      <label className="font-semibold text-gray-700 text-sm mb-2">Proof of Identity </label>
                      <div className="border border-gray-300 rounded-lg p-3 bg-gray-50 flex flex-col items-center justify-center min-h-[100px]">
                        <input
                          type="file"
                          id="proofOfIdentity"
                          accept=".pdf,application/pdf"
                          onChange={(e) => handleDocumentUpload('proofOfIdentity', e.target.files[0], e.target)}
                          className="hidden"
                        />
                        <button
                          type="button"
                          className="px-4 py-2 bg-white border border-gray-400 rounded text-sm font-medium hover:bg-gray-100 mb-2"
                          onClick={() => document.getElementById('proofOfIdentity')?.click()}
                        >
                          Choose File
                        </button>
                        <p className="text-xs text-gray-500 text-center">
                          {documents.proofOfIdentity ? (
                            <span className="text-green-600 font-medium flex flex-col items-center gap-0.5">
                              {documents.proofOfIdentity.source === 'digilocker' && <span className="text-[10px] bg-blue-100 text-blue-700 px-1 rounded">Verified</span>}
                              <span className="truncate max-w-[80px]">{documents.proofOfIdentity.name || '✓ Uploaded'}</span>
                            </span>
                          ) : (
                            'Not uploaded'
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Proof of Address */}
                    <div className="flex flex-col">
                      <label className="font-semibold text-gray-700 text-sm mb-2">Proof of Address </label>
                      <div className="border border-gray-300 rounded-lg p-3 bg-gray-50 flex flex-col items-center justify-center min-h-[100px]">
                        <input
                          type="file"
                          id="proofOfAddress"
                          accept=".pdf,application/pdf"
                          onChange={(e) => handleDocumentUpload('proofOfAddress', e.target.files[0], e.target)}
                          className="hidden"
                        />
                        <button
                          type="button"
                          className="px-4 py-2 bg-white border border-gray-400 rounded text-sm font-medium hover:bg-gray-100 mb-2"
                          onClick={() => document.getElementById('proofOfAddress')?.click()}
                        >
                          Choose File
                        </button>
                        <p className="text-xs text-gray-500 text-center">
                          {documents.proofOfAddress ? (
                            <span className="text-green-600 font-medium flex flex-col items-center gap-0.5">
                              {documents.proofOfAddress.source === 'digilocker' && <span className="text-[10px] bg-blue-100 text-blue-700 px-1 rounded">Verified</span>}
                              <span className="truncate max-w-[80px]">{documents.proofOfAddress.name || '✓ Uploaded'}</span>
                            </span>
                          ) : (
                            'Not uploaded'
                          )}
                        </p>
                      </div>
                    </div>

                    {/* PAN Card */}
                    <div className="flex flex-col">
                      <label className="font-semibold text-gray-700 text-sm mb-2">PAN Card </label>
                      <div className="border border-gray-300 rounded-lg p-3 bg-gray-50 flex flex-col items-center justify-center min-h-[100px]">
                        <input
                          type="file"
                          id="panCard"
                          accept=".pdf,application/pdf"
                          onChange={(e) => handleDocumentUpload('panCard', e.target.files[0], e.target)}
                          className="hidden"
                        />
                        <button
                          type="button"
                          className="px-4 py-2 bg-white border border-gray-400 rounded text-sm font-medium hover:bg-gray-100 mb-2"
                          onClick={() => document.getElementById('panCard')?.click()}
                        >
                          Choose File
                        </button>
                        <p className="text-xs text-gray-500 text-center">
                          {documents.panCard ? (
                            <span className="text-green-600 font-medium flex flex-col items-center gap-0.5">
                              {documents.panCard.source === 'digilocker' && <span className="text-[10px] bg-blue-100 text-blue-700 px-1 rounded">Verified</span>}
                              <span className="truncate max-w-[80px]">{documents.panCard.name || '✓ Uploaded'}</span>
                            </span>
                          ) : (
                            'Not uploaded'
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Cancelled Cheque */}
                    <div className="flex flex-col">
                      <label className="font-semibold text-gray-700 text-sm mb-2">Cancelled Cheque <span className="text-red-500">*</span></label>
                      <div className="border border-gray-300 rounded-lg p-3 bg-gray-50 flex flex-col items-center justify-center min-h-[100px]">
                        <input
                          type="file"
                          id="cancelledCheque"
                          accept=".pdf,application/pdf"
                          onChange={(e) => handleDocumentUpload('cancelledCheque', e.target.files[0], e.target)}
                          className="hidden"
                        />
                        <button
                          type="button"
                          className="px-4 py-2 bg-white border border-gray-400 rounded text-sm font-medium hover:bg-gray-100 mb-2"
                          onClick={() => document.getElementById('cancelledCheque')?.click()}
                        >
                          Choose File
                        </button>
                        <p className="text-xs text-gray-500 text-center">
                          {documents.cancelledCheque ? (
                            <span className="text-green-600 font-medium flex flex-col items-center gap-0.5">
                              <span className="truncate max-w-[80px]">{documents.cancelledCheque.name || '✓ Uploaded'}</span>
                            </span>
                          ) : (
                            'Not uploaded'
                          )}
                        </p>
                      </div>
                    </div>

                    {/* GST Certificate */}
                    <div className="flex flex-col">
                      <label className="font-semibold text-gray-700 text-sm mb-2">GST Certificate </label>
                      <div className="border border-gray-300 rounded-lg p-3 bg-gray-50 flex flex-col items-center justify-center min-h-[100px]">
                        <input
                          type="file"
                          id="gstCertificate"
                          accept=".pdf,application/pdf"
                          onChange={(e) => handleDocumentUpload('gstCertificate', e.target.files[0], e.target)}
                          className="hidden"
                        />
                        <button
                          type="button"
                          className="px-4 py-2 bg-white border border-gray-400 rounded text-sm font-medium hover:bg-gray-100 mb-2"
                          onClick={() => document.getElementById('gstCertificate')?.click()}
                        >
                          Choose File
                        </button>
                        <p className="text-xs text-gray-500 text-center">
                          {documents.gstCertificate ? (
                            <span className="text-green-600 font-medium flex flex-col items-center gap-0.5">
                              <span className="truncate max-w-[80px]">{documents.gstCertificate.name || '✓ Uploaded'}</span>
                            </span>
                          ) : (
                            'Not uploaded'
                          )}
                        </p>
                      </div>
                    </div>

                  </div>

                  {/* Button Row */}
                  <div className="flex justify-between mt-6">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(2)}
                      className="px-8 py-3 rounded-lg font-semibold text-gray-600 border border-gray-300 hover:bg-gray-50 bg-white"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      className="px-8 py-3 rounded-lg font-semibold text-white bg-primary-600 hover:bg-primary-700 transition-all duration-300"
                    >
                      Next: Agreement
                    </button>
                  </div>
                </form>
              </motion.div>
            )
          }

          {/* STEP 5: Agreement & e-Sign */}
          {
            currentStep === 4 && otpVerified && !submissionSuccess && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white shadow-lg rounded-2xl border border-gray-200 p-8"
              >
                <h3 className="text-2xl font-bold text-primary-600 mb-2">
                  Step 5 — Review Agreement
                </h3>
                <p className="text-gray-600 mb-6">
                  Please review the agreement and acknowledge the terms to submit your application.
                </p>

                <div className="space-y-6">

                  {/* Agreement Preview */}
                  <div>
                    <h4 className="font-bold text-lg mb-3">DSA Agreement Preview</h4>
                    <div className="border border-gray-300 rounded-lg p-6 bg-gray-50 text-sm text-gray-700">
                      <div className="space-y-4">
                        <p>This Business Associate Agreement is made between:</p>
                        <p><strong>Aham Housing Finance Limited</strong>, a company incorporated under the Companies Act, having its registered office at Bengaluru (hereinafter referred to as the "Company")</p>
                        <p>AND</p>
                        <p><strong>{registrationData.fullName}</strong>, residing at {registrationData.address}, {registrationData.city} (hereinafter referred to as the "Associate")</p>
                        <p className="text-xs pt-4 border-t border-gray-200">
                          <strong>Associate Details:</strong><br />
                          PAN: {basicData.panNumber} | Aadhaar: {maskAadhaar(basicData.aadhaarNumber)}<br />
                          Bank: {bankData.bankBranch} | A/c: {bankData.accountNumber?.substring(0, 4)}xxxx{bankData.accountNumber?.slice(-4)}
                        </p>
                        <p className="pt-4 font-medium italic">By signing this agreement, the Associate agrees to comply with the Company's Code of Conduct and sourcing policies.</p>
                      </div>
                    </div>
                  </div>

                  {/* Agreement Checkbox */}
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="agreeTerms"
                      required
                      className="mt-1 h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="agreeTerms" className="text-sm text-gray-700">
                      I have read and agree to the DSA Agreement
                    </label>
                  </div>

                  {/* Button Row */}
                  <div className="flex justify-between mt-6">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(3)}
                      className="px-8 py-3 rounded-lg font-semibold text-gray-600 border border-gray-300 hover:bg-gray-50 bg-white"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={handleFinalSubmit}
                      disabled={isSubmitting}
                      className={`px-8 py-3 rounded-lg font-semibold text-white transition-all duration-300 ${isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-700'}`}
                    >
                      {isSubmitting ? 'Submitting...' : 'Submitted'}
                    </button>
                  </div>

                </div>
              </motion.div>
            )
          }

          {/* SUCCESS SCREEN */}
          {
            submissionSuccess && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white shadow-lg rounded-2xl border border-gray-200 p-12 text-center max-w-2xl mx-auto"
              >
                <div className="mb-6">
                  <CheckCircleIcon className="h-20 w-20 text-green-500 mx-auto mb-4" />
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">
                    Application Submitted Successfully
                  </h2>
                  <p className="text-gray-600">
                    Your application is now pending internal verification. You will receive an Email with your branch details.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6 mb-6 text-left">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Assigned Branch:</p>
                    <p className="text-lg font-semibold text-gray-900">{assignedBranch}</p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={downloadSummary}
                  className="px-8 py-3 rounded-lg font-semibold text-white bg-primary-600 hover:bg-primary-700 transition-all duration-300 mb-4"
                >
                  Download Summary (PDF)
                </button>

                <div className="mt-6">
                  <p className="text-xs text-gray-400">
                    © {new Date().getFullYear()} Aham Housing Finance Limited. Official Partner Registration.
                  </p>
                </div>
              </motion.div>
            )
          }

          {/* Copyright Footer */}
          {/* <div className="mt-8 text-center">
            <p className="text-gray-400 text-sm">© 2025 Aham Housing Finance Limited. All rights reserved. Powered by <a href="/" className="text-primary-600 hover:text-primary-700">MBW</a></p>
          </div> */}

        </div >
      </section >

      {/* Sub Menu 9: FAQs */}
      < section id="faq" className="py-20 bg-gray-50" >
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
              {faqSettings?.section_title || 'Frequently Asked Questions'}
            </h2>
            <p className="text-xl text-gray-600">
              {faqSettings?.section_subtitle || 'Get answers to common questions about our partner program'}
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto space-y-4">
            {displayFaqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl shadow-soft overflow-hidden"
              >
                <button
                  onClick={() => setActiveFAQ(activeFAQ === index ? -1 : index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors duration-200"
                >
                  <span className="text-lg font-semibold text-gray-900 pr-4">
                    {faq.question}
                  </span>
                  {activeFAQ === index ? (
                    <ChevronUpIcon className="h-6 w-6 text-primary-600 flex-shrink-0" />
                  ) : (
                    <ChevronDownIcon className="h-6 w-6 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {activeFAQ === index && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="px-6 pb-6"
                  >
                    <p className="text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section >

      {/* CTA Banner */}
      <section className="py-20 bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url(img/ahamlife/Partner-Banner.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }} />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center text-white"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              {ctaBannerSettings.title}
            </h2>
            <p className="text-xl mb-4 max-w-2xl mx-auto text-white/90">
              {ctaBannerSettings.description}
            </p>
            {(ctaBannerSettings.bottom_text || ctaBannerSettings.bottom_text === undefined) && (
              <p
                onClick={handleRichTextClick}
                className="text-lg md:text-xl mb-10 max-w-3xl mx-auto text-white/95 leading-relaxed [&_a]:text-yellow-400 [&_a]:underline hover:[&_a]:text-yellow-300 [&_a]:font-semibold [&_a]:transition-colors"
                dangerouslySetInnerHTML={{
                  __html: sanitizeAndFormatHtml(
                    ctaBannerSettings.bottom_text ||
                    'Ready to partner with Aham? Complete your <a href="https://ahamhfc.com/home-loan-dsa-registration-online">Home Loan DSA registration online</a> to get started.'
                  )
                }}
              />
            )}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <motion.button
                onClick={() => setIsEnquiryModalOpen(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-primary-600 hover:bg-primary-50 px-8 py-2.5 rounded-xl font-bold text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[70%] sm:w-auto mx-auto"
              >
                {ctaBannerSettings.button_text}
                <ArrowRightIcon className="h-6 w-6" />
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Enquiry Modal */}
      < EnquiryModal
        isOpen={isEnquiryModalOpen}
        onClose={() => setIsEnquiryModalOpen(false)}
      />

    </div >
  );
};

export default DSAPartnerProgram;
